import { Component, computed, input, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { TooltipModule } from 'primeng/tooltip';
import { DividerModule } from 'primeng/divider';

import { RegionalConfigService } from '@admin/modules/regional-configs/services/regional-configs.service';
import { RegionalConfigsModel } from '@admin/modules/regional-configs/model/regional-configs.model';

@Component({
  selector: 'app-regional-config-info',
  standalone: true,
  imports: [CommonModule, TranslateModule, TooltipModule, DividerModule],
  templateUrl: './regional-config-info.component.html',
})
export class RegionalConfigInfoComponent implements OnInit {
  collectorIds = input<string[]>([]);
  campaignType = input<string | undefined>();

  accordionVisible = signal<boolean>(true);
  regionalConfigs = signal<RegionalConfigsModel[]>([]);

  constructor(
    private regionalConfigService: RegionalConfigService,
    private translate: TranslateService,
  ) {}

  displayedConfigs = computed(() => {
    const ids = this.collectorIds();
    const configs = this.regionalConfigs();

    if (!configs.length) return [];

    const isDripWithCollectors = this.campaignType() === 'drip' && ids.length > 0;

    const result: any[] = [];

    configs.forEach((config) => {
      const { timezone, timetable, collectors = [], description } = config;

      let matchedCollectors = collectors;

      if (isDripWithCollectors) {
        matchedCollectors = collectors.filter((c: any) => ids.includes(c.id));
      }

      if (matchedCollectors.length > 0) {
        result.push({
          timezone,
          description,
          collectors: matchedCollectors,
          timetable: {
            MORNING: timetable?.MORNING || null,
            EVENING: timetable?.EVENING || null,
            FULL_DAY: timetable?.FULL_DAY || null,
          },
        });
      }
    });

    return result;
  });

  ngOnInit(): void {
    this.loadRegionalConfigs();
  }

  toggleAccordion(): void {
    this.accordionVisible.update((visible) => !visible);

    if (this.accordionVisible() && !this.regionalConfigs().length) {
      this.loadRegionalConfigs();
    }
  }

  private loadRegionalConfigs(): void {
    this.regionalConfigService.listRegionalConfigs().subscribe({
      next: (response: any) => {
        const configs = response?.data?.regionalConfigs || [];
        this.regionalConfigs.set(configs);
      },
      error: (error) => {
        console.error('Error loading regional configs', error);
      },
    });
  }

  getTimetableEntries(timetable: any): any[] {
    if (!timetable) return [];

    const order = ['MORNING', 'EVENING', 'FULL_DAY'];

    return Object.entries(timetable)
      .map(([key, value]: [string, any]) => ({
        key,
        label: this.mapPeriodLabel(key),
        start: value?.start,
        finish: value?.finish || value?.end,
      }))
      .sort((a, b) => order.indexOf(a.key) - order.indexOf(b.key));
  }

  private mapPeriodLabel(key: string): string {
    const labels: { [key: string]: string } = {
      MORNING: this.translate.instant('DATA.COMMON.TIME_OF_WEEK.MORNING'),
      EVENING: this.translate.instant('DATA.COMMON.TIME_OF_WEEK.EVENING'),
      FULL_DAY: this.translate.instant('DATA.COMMON.TIME_OF_WEEK.FULL_DAY'),
    };
    return labels[key] || key;
  }

  getCollectorNames(group: any): string {
    if (group.collectors?.length) {
      return group.collectors.map((c: any) => c.name).join(', ');
    }
    return group.description || 'Tenant Config';
  }

  getCollectorNamesShort(group: any): string {
    const full = this.getCollectorNames(group);
    return full.length > 35 ? full.slice(0, 35) + '…' : full;
  }
}
